import { useNavigate } from 'react-router-dom';

import { observer, useLocalObservable } from 'mobx-react-lite';

import { Button } from '@components/button';
import { RadioGroup } from '@components/radio-group';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { Grid, GridColumn } from 'components/Grid';
import { Loading } from 'components/Loading';
import { configs } from 'jobboards/configs';
import { useStore } from 'models/Provider';

export const PromotePost = observer(({ job, selectedBoard, resetSelectedBoard }) => {
  const { t, api, jobboardStore } = useStore();
  const { showSuccess, showError } = useToaster();
  const navigate = useNavigate();

  const config = useLocalObservable(() => configs[selectedBoard]);
  const { Form, Example, useBoardState, title } = config;
  const boardState = useBoardState(job);

  const state = useLocalObservable(() => ({
    language: job.languages[0].language,
    submitting: false,
    editLanguage(value) {
      state.language = value;
    },
    get languageOptions() {
      return job.languages.map((lang) => ({
        value: lang.language,
        label: t(`general.${lang.language}`),
      }));
    },
    get data() {
      return job.languages.find((lang) => lang.language === state.language);
    },
    get disableSubmit() {
      return boardState.disableSubmit || state.submitting;
    },
    async submit() {
      if (state.disableSubmit) return;
      state.setSubmitting(true);

      const res = await api.jobboards.createJobPost({
        job: job.id,
        jobBoard: selectedBoard,
        language: state.language,
        form: boardState.form,
      });
      if (!res.error) {
        await jobboardStore.reload();
        showSuccess(t('jobboards.submit.success', state.data.title, configs[selectedBoard].title));
        navigate(`/jobs/${job.id}/promote`, { replace: true });
        return;
      }
      showError(t('jobboards.submit.error', state.data.title, configs[selectedBoard].title));
      state.setSubmitting(false);
    },
    setSubmitting(val) {
      state.submitting = val;
    },
  }));

  return (
    <Grid>
      {state.submitting && <Loading />}
      <GridColumn span="4">
        <Stack space={6}>
          <Text font="heading2" style={{ minHeight: '4rem' }}>
            {t('jobboards.post.heading', title)}
          </Text>
          <Separator />
          {state.languageOptions.length > 1 && (
            <>
              <RadioGroup
                label={t('jobboards.post.language')}
                options={state.languageOptions}
                onChange={state.editLanguage}
                defaultValue={state.language}
              />
              <Separator />
            </>
          )}
          <Form boardState={boardState} state={state} />
          <Stack orientation="horizontal" space={4} justify="flex-end">
            <Button
              variant="secondary"
              onClick={resetSelectedBoard}
              isDisabled={state.submitting}
              text={t('jobboards.post.back')}
            />
            <Button
              isDisabled={state.disableSubmit}
              text={t('jobboards.post.go')}
              onClick={state.submit}
            />
          </Stack>
        </Stack>
      </GridColumn>
      <GridColumn span="8">
        <Text font="heading2" style={{ minHeight: '4rem' }}>
          {t('jobboards.post.example')}
        </Text>
        <Example title={title} boardState={boardState} job={job} state={state} />
      </GridColumn>
    </Grid>
  );
});
